import { useAdaptivePolling } from "@/hooks/use-adaptive-polling";
import { cn } from "@/lib/utils";
import { useChunkError } from "@/providers/chunk-error";
import { useDiabolo } from "@/providers/diabolocom";
import { useRingover } from "@/providers/ringover";
import { useSms } from "@/providers/sms";
import { useUser } from "@/providers/user";
import { CoachState, useWalterCoach } from "@/providers/walter-coach";
import { DataService } from "@/services/data";
import { Brain, MessagesSquare, PhoneOutgoing, RefreshCw, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SMSConversations } from "./sms";
import { WalterIaChatPanel } from "./walter-assistant/chat-panel";
import { WalterIaCoachPanel } from "./walter-assistant/coach-panel";
import { resolveCoachStatusAccent } from "./walter-assistant/live-coach-signal.utils";
import { takeWalterIaOpenRequest } from "./walter-assistant/open-request";

/**
 * Mappe l'état brut du provider Walter Coach vers les clés de statut connues
 * du `LiveCoachSignal`. On le duplique ici (au lieu de réutiliser la version
 * locale au `coach-panel`) pour éviter une dépendance croisée, mais la logique
 * reste alignée : tout état non-ouvert retombe sur `not_connected` (palette
 * grise, message "lance ton app desktop"). Le mode `open_post_call`
 * (recap) bascule sur la palette `completed` — vert atténué pour
 * signaler "il s'est passé qqc, tu peux relire" sans imiter l'écoute live.
 */
function coachStateToSignalStatus(state: CoachState): "not_connected" | "idle" | "on_call" | "completed" {
    switch (state) {
        case "open_in_call":
            return "on_call";
        case "open_post_call":
            return "completed";
        case "open_idle":
            return "idle";
        case "not_connected":
        default:
            return "not_connected";
    }
}

export function UtilityBar() {
    const { open: diabolocomOpen, setOpen: setDiabolocomOpen, crmPopupData } = useDiabolo();
    const { open: ringoverOpen, setOpen: setRingoverOpen } = useRingover();
    const { user } = useUser();
    const { hasChunkError, reloadPage } = useChunkError();

    const navigate = useNavigate();
    const [smsOpen, setSmsOpen] = useState(false);
    const [unreadConversationsCount, setUnreadConversationsCount] = useState<number>(0);
    const displayUnread = unreadConversationsCount > 99 ? "99+" : String(unreadConversationsCount);
    const [aiChatOpen, setAiChatOpen] = useState(false);
    const [aiCoachOpen, setAiCoachOpen] = useState(false);
    const { isOpen: smsIsOpen, setIsOpen: setSmsIsOpen } = useSms();
    const { coachState } = useWalterCoach();
    /*
     * En `open_in_call` ET `open_post_call` (recap) on garde le panel
     * grand format (70dvh) : le recap a besoin de la même place pour
     * dérouler la roadmap, les objections et la liste des suggestions.
     */
    const isCoachCallActive = coachState === "open_in_call" || coachState === "open_post_call";
    const isCoachPostCall = coachState === "open_post_call";
    /*
     * On aligne la pastille de statut sur la palette du `LiveCoachSignal` pour
     * que l'utilisateur reconnaisse instantanément l'état du coach (gris en
     * connexion, violet en idle, vert en appel, vert atténué en recap) —
     * exactement la même couleur que celle qu'il verra dans le panneau
     * étendu.
     */
    const coachStatusAccent = resolveCoachStatusAccent(coachStateToSignalStatus(coachState));

    // Opens Walter IA once the user is known, when the portal asked for it.
    useEffect(() => {
        if (!user || !takeWalterIaOpenRequest()) return;
        if (user.walter_assistant_profile) {
            setAiChatOpen(true);
            setAiCoachOpen(false);
        }
    }, [user]);

    // Synced during render so effect cleanup can tell hangup (ref cleared → keep
    // retry for late navigate) from a newer CTI cycle still holding popup data.
    const crmPopupDataRef = useRef(crmPopupData);
    crmPopupDataRef.current = crmPopupData;
    // Monotonic id bumped during render on each new non-null crmPopupData reference
    // so an in-flight resolve becomes stale before the previous effect cleans up.
    // Hangup (undefined) does not bump: the latest cycle may still late-navigate.
    const ctiRequestIdRef = useRef(0);
    const ctiArmedPopupRef = useRef(crmPopupData);
    if (crmPopupData && crmPopupData !== ctiArmedPopupRef.current) {
        ctiRequestIdRef.current += 1;
        ctiArmedPopupRef.current = crmPopupData;
    } else if (!crmPopupData) {
        ctiArmedPopupRef.current = undefined;
    }

    useEffect(() => {
        if (!crmPopupData) return;

        type CtiResponse = { crm_popup_id: string; status: "bad_request" | "forbidden" | "not_found" | "ok"; url: string | null };
        type CtiFailureStatus = Exclude<CtiResponse["status"], "ok">;

        const requestId = ctiRequestIdRef.current;
        // `active` covers Strict Mode remounts (same requestId); requestId covers
        // A→B races where the next cycle bumps during render before this cleanup.
        let active = true;
        const isStale = () => !active || requestId !== ctiRequestIdRef.current;

        // Snapshot this call so the POST body stays stable for the cycle.
        const snapshot = {
            campaign_id: crmPopupData.campaign_id,
            phone_number: crmPopupData.phone_number,
            crm_popup_id: crmPopupData.crm_popup_id
        };

        const toastForCtiFailure = (status: CtiFailureStatus) => {
            if (status === "forbidden") {
                toast.error("Vous n'avez pas accès à la fiche liée à cet appel");
            } else if (status === "not_found") {
                toast.warning("Aucune fiche trouvée pour cet appel");
            } else {
                toast.warning("Impossible d'ouvrir la fiche liée à cet appel");
            }
        };

        let retryTimer: ReturnType<typeof setTimeout> | null = null;

        const fetchCtiPopup = () => DataService.client.url("/voice/cti/").post(snapshot).json<CtiResponse>();

        // Un seul retry après 1s en cas d'échec réseau / 5xx, pour amortir les
        // petites latences côté Salesforce sur la résolution du popup id.
        fetchCtiPopup()
            .catch(
                () =>
                    new Promise<CtiResponse>((resolve, reject) => {
                        retryTimer = setTimeout(() => {
                            retryTimer = null;
                            if (isStale()) {
                                reject(new Error("cti retry cancelled"));
                                return;
                            }
                            fetchCtiPopup().then(resolve, reject);
                        }, 1000);
                    })
            )
            .then(rep => {
                if (isStale()) return;
                if (rep.status == "ok" && rep.url) {
                    navigate(rep.url);
                    toast.info("Voici la fiche liée à votre appel");
                } else if (rep.status !== "ok") {
                    console.warn(`/voice/cti/ a renvoyé status=${rep.status}`, snapshot);
                    toastForCtiFailure(rep.status);
                }
            })
            .catch(error => {
                if (isStale()) return;
                console.error("Echec de la résolution de la fiche CTI", error);
            });

        return () => {
            // Hangup clears the ref before cleanup: keep active/retry for late navigate.
            // A newer cycle (other call, same-call re-run, Strict remount) leaves popup
            // data set: deactivate this cycle and abort its pending retry.
            if (crmPopupDataRef.current) {
                active = false;
                if (retryTimer) {
                    clearTimeout(retryTimer);
                    retryTimer = null;
                }
            }
        };
    }, [crmPopupData]);

    useAdaptivePolling(
        async controller => {
            try {
                const data = await DataService.client
                    .url("/crm/app/conversations/notifications/")
                    .signal(controller)
                    .get()
                    .json<{ unread_conversations_count: number }>();
                setUnreadConversationsCount(data.unread_conversations_count || 0);
            } catch (error) {
                if (!controller.signal.aborted) {
                    setUnreadConversationsCount(0);
                }
                throw error;
            }
        },
        { intervalMs: 15_000, maxBackoffMs: 60_000 }
    );

    // Sync local open state with SMS context (so opening from context reflects in the panel visibility)
    useEffect(() => {
        if (smsOpen !== smsIsOpen) {
            setSmsOpen(smsIsOpen);
            if (smsIsOpen) {
                setDiabolocomOpen(false);
                setRingoverOpen(false);
                setAiChatOpen(false);
                setAiCoachOpen(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [smsIsOpen]);

    return (
        <div data-utility-bar className="fixed bottom-0 left-0 z-50 flex h-11 w-full items-center justify-between border-t-2 bg-white shadow-lg">
            <div className="flex h-full items-start px-6">
                {(user?.sf_user?.diabolocom_user_id || user?.sf_user?.ringover_user_id) && (
                    <>
                        {user?.sf_user?.diabolocom_user_id && (
                            <div className="flex h-full items-start gap-4 ">
                                <span
                                    className={cn("flex h-full cursor-pointer items-center gap-2 px-4 transition-colors duration-200", diabolocomOpen ? "bg-gray-100" : "hover: hover:bg-gray-100")}
                                    onClick={() => {
                                        setDiabolocomOpen(!diabolocomOpen);
                                        setRingoverOpen(false);
                                        setSmsOpen(false);
                                        setAiChatOpen(false);
                                        setAiCoachOpen(false);
                                    }}
                                >
                                    <PhoneOutgoing className="inline-block size-4 text-black" />
                                    <span className="text-sm font-medium">Diabolocom</span>
                                </span>

                                <div
                                    className={cn(
                                        "absolute bottom-11 left-2 rounded-md border border-b-0 bg-white p-1 shadow-lg",
                                        "transition-all duration-300 ease-in-out",
                                        diabolocomOpen ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0"
                                    )}
                                >
                                    {/* MANUAL LOGIN https://fr10.engage.diabolocom.com/crm-sso/walter_learning/default?dblSsoInWindow=true */}
                                    {/* SSO https://fr10.engage.diabolocom.com/app/agent-crm/walter_learning?default&dblSsoAccount=walter_learning&dblSsoInWindow=true */}
                                    <iframe
                                        id="diabolocom-cti-iframe"
                                        src="https://fr10.engage.diabolocom.com/app/agent-crm/walter_learning?default&dblSsoAccount=walter_learning&dblSsoInWindow=true"
                                        title="diabolocom-cti-iframe"
                                        name="diabolocom-cti-iframe"
                                        allow="microphone; camera"
                                        height="400"
                                        width="400"
                                    ></iframe>
                                </div>
                            </div>
                        )}
                        {(user?.sf_user?.diabolocom_user_id || user?.sf_user?.ringover_user_id) && (
                            <div className="flex h-full items-start gap-4 ">
                                <span
                                    className={cn("flex h-full cursor-pointer items-center gap-2 px-4 transition-colors duration-200", ringoverOpen ? "bg-gray-100" : "hover: hover:bg-gray-100")}
                                    onClick={() => {
                                        setRingoverOpen(!ringoverOpen);
                                        setSmsOpen(false);
                                        setDiabolocomOpen(false);
                                        setAiChatOpen(false);
                                        setAiCoachOpen(false);
                                    }}
                                >
                                    <PhoneOutgoing className="inline-block size-4 text-black" />
                                    <span className="unselectable text-sm font-medium">Ringover</span>
                                </span>

                                <div
                                    className={cn(
                                        "absolute bottom-11 left-2 rounded-md border border-b-0 bg-white p-1 shadow-lg",
                                        "transition-all duration-300 ease-in-out",
                                        ringoverOpen ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0"
                                    )}
                                >
                                    <div id="ringover-cti-container" className="h-[600px] w-[350px]" />
                                </div>
                            </div>
                        )}
                    </>
                )}
                <div className="flex h-full items-start gap-4 ">
                    <span
                        className={cn("flex h-full  cursor-pointer items-center justify-center gap-2 px-4 transition-colors duration-200", smsOpen ? "bg-gray-100" : "hover: hover:bg-gray-100")}
                        onClick={() => {
                            const next = !smsOpen;
                            setSmsOpen(next);
                            setSmsIsOpen(next);
                            setDiabolocomOpen(false);
                            setRingoverOpen(false);
                            setAiChatOpen(false);
                            setAiCoachOpen(false);
                        }}
                    >
                        <div className="relative inline-flex items-center">
                            <MessagesSquare className="inline-block size-4 text-black" />
                            {unreadConversationsCount > 0 && (
                                <span
                                    title={`${unreadConversationsCount} unread conversations`}
                                    className="absolute -right-1 -top-1 inline-flex h-3 min-w-[12px] items-center justify-center rounded-full bg-red-600 px-0.5 text-[9px] font-semibold leading-none text-white ring-1 ring-white"
                                >
                                    {displayUnread}
                                </span>
                            )}
                        </div>
                        <span className="unselectable text-sm font-medium">SMS Conversations</span>
                    </span>
                    <div
                        className={cn(
                            "absolute bottom-11 left-2 h-[60dvh] w-[70dvw] max-w-[700px] rounded-md border border-b-0 bg-white p-0 shadow-lg",
                            "transition-all duration-300 ease-in-out",
                            smsOpen ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0"
                        )}
                    >
                        <SMSConversations isOpen={smsOpen} />
                    </div>
                </div>
            </div>
            <div className="flex h-full shrink-0 items-stretch px-6">
                {/* Bouton de rechargement si erreur de chunk */}
                {hasChunkError && (
                    <div
                        className="flex h-full cursor-pointer items-center gap-2 bg-orange-100 px-4 transition-colors duration-200 hover:bg-orange-200"
                        onClick={reloadPage}
                        title="A new version is available. Click to reload the page."
                    >
                        <RefreshCw className="inline-block size-4 text-orange-700" />
                        <span className="text-sm font-medium text-orange-700">New Version - reload page</span>
                    </div>
                )}
                <span
                    className={cn(
                        "relative flex h-full items-center justify-center gap-2 px-4 transition-colors duration-200",
                        user?.walter_assistant_profile ? "cursor-pointer" : "cursor-not-allowed opacity-70",
                        aiChatOpen ? "bg-gray-100" : "hover: hover:bg-gray-100"
                    )}
                    onClick={() => {
                        if (!user?.walter_assistant_profile) return;
                        const next = !aiChatOpen;
                        setAiChatOpen(next);
                        if (next) setAiCoachOpen(false);
                        setDiabolocomOpen(false);
                        setRingoverOpen(false);
                        setSmsOpen(false);
                        setSmsIsOpen(false);
                    }}
                    title={user?.walter_assistant_profile ? "Open Walter IA" : "Walter IA is not enabled for your user"}
                >
                    <Brain className="inline-block size-4 text-black" />
                    <span className="unselectable text-sm font-medium">Walter IA</span>
                    {!user?.walter_assistant_profile && <span className="absolute -right-1 top-1 rounded-sm bg-gray-600 px-1 text-xs text-white">Off</span>}
                </span>
                {user?.walter_assistant_coach ? (
                    <span
                        className={cn(
                            "relative flex h-full cursor-pointer items-center justify-center gap-2 px-4 transition-colors duration-200",
                            aiCoachOpen ? "bg-gray-100" : "hover: hover:bg-gray-100"
                        )}
                        onClick={() => {
                            const next = !aiCoachOpen;
                            setAiCoachOpen(next);
                            if (next) setAiChatOpen(false);
                            setDiabolocomOpen(false);
                            setRingoverOpen(false);
                            setSmsOpen(false);
                            setSmsIsOpen(false);
                        }}
                        title="Open Coach live"
                    >
                        <Zap className={cn("inline-block size-4 text-black")} />
                        <span className="unselectable relative inline-flex items-center text-sm font-medium">
                            Live Coach
                            <span className="absolute -right-2 -top-1 flex size-2">
                                {/*
                                 * Halo "ping" actif uniquement en appel live (`open_in_call`).
                                 * En recap post-appel (`open_post_call`) on ne ping pas
                                 * pour ne pas suggérer un appel en cours — la pastille
                                 * reste colorée mais statique, palette atténuée.
                                 */}
                                {isCoachCallActive && !isCoachPostCall && (
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ backgroundColor: coachStatusAccent.glow }} />
                                )}
                                <span className="relative inline-flex size-2 rounded-full" style={{ backgroundColor: coachStatusAccent.stroke }} />
                            </span>
                        </span>
                    </span>
                ) : null}
                <div
                    className={cn(
                        "absolute bottom-11 right-2 h-[80dvh] w-[80dvw] max-w-[600px] rounded-md border border-b-0 bg-white p-0 shadow-lg",
                        "transition-all duration-300 ease-in-out",
                        aiChatOpen ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0"
                    )}
                >
                    <WalterIaChatPanel isOpen={aiChatOpen} user={user} />
                </div>
                {/*
                 * Coach panel : la hauteur suit l'état de l'appel.
                 * - Hors appel : le panneau se contente d'afficher le `LiveCoachSignal`
                 *   (~94px de contenu + padding) → on garde ~110px pour un peu d'air.
                 * - En appel : le panneau s'agrandit à 70dvh pour laisser la place
                 *   à la liste de suggestions.
                 * La transition `transition-all` couvre déjà la propriété `height`.
                 */}
                <div
                    className={cn(
                        "absolute bottom-11 right-2 w-[280px] rounded-md border border-b-0 bg-white p-0 shadow-lg",
                        "transition-all duration-500 ease-in-out",
                        aiCoachOpen ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-2 opacity-0",
                        isCoachCallActive ? "h-[70dvh] " : coachState === "not_connected" ? "h-[320px] " : "h-[110px] "
                    )}
                >
                    <WalterIaCoachPanel isOpen={aiCoachOpen} user={user} />
                </div>
            </div>
        </div>
    );
}
